import type { JsonDataItem } from '@myosh/odin-components';
import { Button } from '@myosh/odin-components';
import clsx from 'clsx';
import dot from 'dot-object';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { ListBoxItem } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { RecordLinkEntity } from '../../@types/linked-records-field';
import { resolveFieldDisplayValue } from '../../utils/field-property-util';
import OdinLoadingIndicator from '../common/odin-loading-indicator.component';
import type { GrandScoreFieldType } from '../data-grid-data-cells/grand-score-data-cell';
import { transformGrandScoreCellData } from '../data-grid-data-cells/grand-score-data-cell';
import { transformQuestionnaireCellData } from '../data-grid-data-cells/questionnaire-data-cell';
import TitleValueField from '../fields/linked-records/components/mobile/title-value-field';
import { constructRecordTitle } from '../record/util/record-component-util';
import { LinkedRecordRowProps } from './odin-data-grid-row-template.component';

export type OdinMobileCardTemplateProps = {
  item: JsonDataItem;
  readOnly?: boolean;
  rowIndicator?: {
    indicator: (item: JsonDataItem, className: string) => ReactNode;
  };
  isRowClickable?: boolean;
  gridColumns: RecordLinkEntity[];
  removeLinkedRecord?: (id: string) => void;
  listOfPermittedRecordIds?: string[];
  pendingPermissions?: string[];
  listKey?: string;
};

export function OdinMobileCardTemplate({
  listOfPermittedRecordIds,
  item,
  readOnly,
  rowIndicator,
  isRowClickable,
  gridColumns,
  removeLinkedRecord,
  pendingPermissions = [],
  listKey,
}: OdinMobileCardTemplateProps) {
  const { t } = useTranslation();

  const hasPermissionsToRead = listOfPermittedRecordIds?.includes(String(item.id));
  const isPending = pendingPermissions.includes(String(item.id));

  const displayColumns = useMemo(() => {
    // display first 5 columns only to avoid overcrowding
    return gridColumns.filter((col) => col.visible).slice(0, 5);
  }, [gridColumns]);

  const itemClassNames = clsx(
    'linked-mobile-item rounded-md odin-border odin-border-gray-4 py-2 shadow-sm hover:shadow-md hover:odin-border-primary-2 transition-all duration-200 outline-none focus-visible:ring-2',
    {
      'cursor-pointer hover:bg-gray-100': isRowClickable,
      'bg-gray-100': readOnly,
    }
  );

  const key = item['id'] as number | string;
  if (hasPermissionsToRead) {
    const statusColor = rowIndicator ? rowIndicator.indicator(item, 'w-1 rounded') : undefined;
    return (
      <ListBoxItem
        id={key}
        key={listKey ? `${listKey}-item-${key}` : key}
        className={itemClassNames}
        textValue={returnRecordTitle(item, LinkedRecordRowProps)}
        isDisabled={!isRowClickable}
      >
        <div className="flex w-full flex-row">
          {statusColor}
          <div className="flex w-full min-w-0 flex-col items-start gap-4 rounded-lg p-2">
            <div className="flex w-full flex-wrap gap-x-4 gap-y-2">
              {displayColumns
                .map((column) => {
                  const valueRendered = getFieldValue(item, column);
                  if (!valueRendered) {
                    return null;
                  }
                  const key = `${item.id}-${column.field}`;
                  return <TitleValueField key={key} title={column.title || column.field} value={valueRendered} />;
                })
                .filter(Boolean)}
            </div>
            {!readOnly && removeLinkedRecord && (
              <div className="flex justify-end">
                <Button
                  size="small"
                  onClick={(ev) => {
                    removeLinkedRecord(String(item.id));
                    ev.stopPropagation();
                  }}
                >
                  {t('remove')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </ListBoxItem>
    );
  } else if (isPending) {
    return (
      <ListBoxItem id={key} key={`${listKey}-${key}`} className={itemClassNames} textValue={t('no-record-permissions')}>
        <div className="flex w-full items-center justify-center px-2">
          <OdinLoadingIndicator />
        </div>
      </ListBoxItem>
    );
  } else {
    return (
      <ListBoxItem id={key} key={`${listKey}-${key}`} className={itemClassNames} textValue={t('no-record-permissions')}>
        <div className="flex w-full items-center justify-center px-2">
          <span className="font-medium text-primary-1">{t('no-record-permissions')}</span>
        </div>
      </ListBoxItem>
    );
  }
}

export const returnRecordTitle = (item: JsonDataItem, recordFields: Record<string, unknown>) => {
  const formName = String(item[recordFields.formName as string]);
  const docNo = String(item[recordFields.docNo as string]);
  const status = String(item[recordFields.status as string]);

  return constructRecordTitle(formName, docNo, status);
};

const getFieldValue = (item: JsonDataItem, column: RecordLinkEntity) => {
  const value = (column.valuePath ? dot.pick(column.valuePath, item) : item[column.field as keyof typeof item]) ?? '';
  if (column.entityType === 'GRAND_SCORE' || column.entityType === 'QUESTIONNAIRE') {
    // summary linked records can contain number as a value for grand score/questionaire fields
    if (!isNaN(Number(value))) {
      return Number(value).toFixed(2);
    }

    const entitySubType = column.entitySubType;
    const isGrandScoreType = column.entityType === 'GRAND_SCORE';
    if (entitySubType && isGrandScoreType) {
      return transformGrandScoreCellData(entitySubType as GrandScoreFieldType, value);
    } else if (entitySubType && !isGrandScoreType && 'result' in value) {
      return transformQuestionnaireCellData(entitySubType, value.result);
    }
    return undefined;
  }

  const resolved = resolveFieldDisplayValue(value);
  if (!resolved || (Array.isArray(resolved) && resolved.length === 0)) {
    return undefined;
  }
  const valueRendered = typeof resolved === 'object' ? JSON.stringify(resolved) : String(resolved);
  return valueRendered;
};
